import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { formatMoney, formatDate, formatDateTime } from "../../lib/money";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Inbox as InboxIcon, MessageSquare, PlusCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  open: "bg-rose-50 text-rose-700",
  answered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-600",
};

function TextAnswerDialog({ open, onOpenChange, request, onAnswered }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setText(""); setError(""); } }, [open]);

  const submit = async () => {
    if (!text.trim()) { setError("Please write a short explanation."); return; }
    setSubmitting(true); setError("");
    try {
      await api.post(`/comment-requests/${request.id}/answer`, { answer_text: text.trim() });
      toast.success("Reply sent");
      onOpenChange(false);
      onAnswered?.();
    } catch (e) { setError(extractError(e)); }
    finally { setSubmitting(false); }
  };

  if (!request) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="text-answer-dialog">
        <DialogHeader>
          <DialogTitle>Reply with a comment</DialogTitle>
          <DialogDescription>
            Tell accounting why this charge isn't yours, or anything they need to know.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Your reply</Label>
          <textarea
            data-testid="answer-textarea"
            className="min-h-[100px] w-full rounded-md border border-slate-200 p-2 text-sm focus:border-[#0F1E3D] focus:outline-none"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Not my charge — never been to that vendor."
          />
        </div>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="submit-text-answer"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            {submitting ? "Sending…" : "Send reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChaseCard({ req, onLogExpense, onTextReply, readOnly }) {
  return (
    <div data-testid={`chase-card-${req.id}`}
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Badge className={`${STATUS_STYLE[req.status] || "bg-slate-100"} hover:opacity-90`}>
              {req.status}
            </Badge>
            <span className="text-xs text-slate-500">
              {formatDate(req.line_occurred_at)} · {req.card_label} ·{" "}
              <span className="font-mono">{req.card_last4}</span>
            </span>
          </div>
          <div className="font-medium text-slate-900" data-testid="chase-desc">
            {req.line_raw_description}
          </div>
          <div className="mt-1 font-mono text-lg font-semibold text-[#0F1E3D]">
            {formatMoney(req.line_amount_cents || 0)}
          </div>
          {req.message && (
            <div className="mt-3 rounded-md border-l-2 border-[#0F1E3D]/30 bg-slate-50 p-2 text-xs text-slate-700">
              <span className="font-semibold">{req.requested_by_name} asked:</span> {req.message}
            </div>
          )}
          {req.status === "answered" && (
            <div className="mt-3 rounded-md bg-emerald-50 p-2 text-xs text-emerald-900">
              {req.answer_expense_id
                ? <span>You linked an expense — line auto-matched.</span>
                : <span>Your reply: <em>"{req.answer_text}"</em></span>}
              <span className="ml-1 text-emerald-700">· {formatDateTime(req.answered_at)}</span>
            </div>
          )}
        </div>
      </div>
      {!readOnly && req.status === "open" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => onLogExpense(req)} data-testid={`btn-log-expense-${req.id}`}
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <PlusCircle className="mr-1 h-4 w-4" /> Log expense for this
          </Button>
          <Button variant="outline" onClick={() => onTextReply(req)} data-testid={`btn-text-reply-${req.id}`}>
            <MessageSquare className="mr-1 h-4 w-4" /> This isn't mine / explain
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Inbox() {
  const { refreshCounters } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [textDialog, setTextDialog] = useState({ open: false, req: null });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/comment-requests", { params: { page_size: 100 } });
      setRequests(r.data?.items || []);
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogExpense = (req) => {
    // Prefill new-expense form via URL params
    const qs = new URLSearchParams({
      card_id: req.card_id,
      amount_cents: String(req.line_amount_cents || 0),
      occurred_at: req.line_occurred_at ? req.line_occurred_at.slice(0, 10) : "",
      vendor: req.line_raw_description || "",
      comment_request_id: req.id,
    });
    navigate(`/expenses/new?${qs.toString()}`);
  };

  const openText = (req) => setTextDialog({ open: true, req });

  const openItems = requests.filter((r) => r.status === "open");
  const historyItems = requests.filter((r) => r.status !== "open");

  return (
    <div data-testid="inbox-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          <InboxIcon className="h-6 w-6 text-[#0F1E3D]" /> Inbox
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Statement charges accounting needs you to confirm or explain.
        </p>
      </div>

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <Tabs defaultValue="open" className="w-full">
        <TabsList data-testid="inbox-tabs">
          <TabsTrigger value="open" data-testid="tab-open">
            Open <Badge variant="secondary" className="ml-2 bg-rose-100 text-rose-800">{openItems.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            History <Badge variant="secondary" className="ml-2">{historyItems.length}</Badge>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="open" className="mt-4">
          {loading ? (
            <div className="py-10 text-center text-slate-500">Loading…</div>
          ) : openItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500">
              <InboxIcon className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              All caught up — no open requests.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {openItems.map((req) => (
                <ChaseCard key={req.id} req={req} onLogExpense={handleLogExpense} onTextReply={openText} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          {historyItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500">
              No history yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {historyItems.map((req) => (
                <ChaseCard key={req.id} req={req} readOnly />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TextAnswerDialog
        open={textDialog.open}
        onOpenChange={(o) => setTextDialog((s) => ({ ...s, open: o }))}
        request={textDialog.req}
        onAnswered={() => { load(); refreshCounters?.(); }}
      />
    </div>
  );
}
